import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Camera, AlertTriangle, Package, Calendar, User } from 'lucide-react';
import { DamageReport, Project, Item, Photo } from '../types';
import { getProject } from '../services/projectService';
import { getItem } from '../services/itemService';
import { getDamageReport, deleteDamageReport } from '../services/damageReportService';
import { getPhotosByItem, getPhotosByProject } from '../services/photoService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import ConfirmDialog from '../components/ConfirmDialog';
import PhotoViewer from '../components/PhotoViewer';
import { PageShell } from '../components/ui/PageShell';
import { SectionGroup } from '../components/ui/SectionGroup';
import { ActionRow } from '../components/ui/ActionRow';
import { StatusBadge } from '../components/ui/StatusBadge';
import { SkeletonList } from '../components/ui/Skeleton';

const DamageReportDetailPage: React.FC = () => {
  const { projectId, reportId } = useParams<{ projectId: string; reportId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [report, setReport] = useState<DamageReport | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [item, setItem] = useState<Item | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [photoLoadError, setPhotoLoadError] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId || !reportId) return;

      try {
        setLoading(true);

        const [fetchedReport, fetchedProject] = await Promise.all([
          getDamageReport(reportId),
          getProject(projectId),
        ]);

        setReport(fetchedReport);
        setProject(fetchedProject);

        const secondaryFetches: Promise<any>[] = [];
        if (fetchedReport.itemId) {
          secondaryFetches.push(
            getItem(fetchedReport.itemId).then(i => setItem(i)).catch(() => {})
          );
        }
        if (fetchedReport.photoIds && fetchedReport.photoIds.length > 0) {
          const photoFetch = (fetchedReport.itemId
            ? getPhotosByItem(projectId, fetchedReport.itemId)
            : getPhotosByProject(projectId)
          ).then(allPhotos => {
            setPhotos(allPhotos.filter(photo => fetchedReport.photoIds.includes(photo.id)));
          }).catch(() => {});
          secondaryFetches.push(photoFetch);
        }
        if (secondaryFetches.length > 0) await Promise.all(secondaryFetches);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte hämta data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, reportId]);

  const handleDelete = () => {
    if (!reportId) return;
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att ta bort skaderapporten.');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!reportId) return;
    try {
      setDeleting(true);
      await deleteDamageReport(reportId);
      setShowDeleteConfirm(false);
      navigate(`/projects/${projectId}/damage-reports`);
    } catch (err) {
      console.error('Error deleting damage report:', err);
      setError('Kunde inte ta bort skaderapporten. Försök igen senare.');
    } finally {
      setDeleting(false);
    }
  };

  const handleImageError = (photoId: string) => {
    setPhotoLoadError(prev => ({ ...prev, [photoId]: true }));
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Okänt datum';
    try {
      const date = timestamp.toDate();
      return date.toLocaleDateString('sv-SE', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Ogiltigt datum';
    }
  };

  if (loading) {
    return (
      <PageShell title="Laddar..." backTo={projectId ? `/projects/${projectId}/damage-reports` : '/'} backLabel="Skador">
        <div className="space-y-4 py-2">
          <SkeletonList count={3} />
          <SkeletonList count={2} />
        </div>
      </PageShell>
    );
  }

  if (error || !report || !project) {
    return (
      <PageShell title="Skaderapport" backTo={projectId ? `/projects/${projectId}/damage-reports` : '/'} backLabel="Skador">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          {error || 'Skaderapporten kunde inte hittas.'}
          {!isOnline && <p className="mt-1 text-xs">Du är offline.</p>}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Skaderapport"
      backTo={`/projects/${projectId}/damage-reports`}
      backLabel="Skador"
      headerRight={
        <button
          onClick={() => navigate(`/projects/${projectId}/damage-reports/${reportId}/edit`)}
          className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity"
        >
          Redigera
        </button>
      }
    >
      <div className="space-y-4 pb-24">
        {/* Report header accent card */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={22} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-0.5">Skaderapport</p>
                <p className="text-[15px] font-medium text-red-700 dark:text-red-400">
                  {project.address.street}, {project.address.city}
                </p>
              </div>
            </div>
            <StatusBadge status={report.status} size="md" />
          </div>
          {item && (
            <div className="mt-2 ml-[56px] flex items-center text-[15px] text-red-600 dark:text-red-400/80">
              <Package size={14} className="mr-1.5" />
              <span>Objekt: {item.name}</span>
            </div>
          )}
        </div>

        {/* Description */}
        <SectionGroup title="Beskrivning av skada">
          <div className="px-4 py-3 text-[15px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">
            {report.description}
          </div>
        </SectionGroup>

        {/* Resolution */}
        {report.resolution && (
          <SectionGroup title="Lösning">
            <div className="px-4 py-3 text-[15px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">
              {report.resolution}
            </div>
          </SectionGroup>
        )}

        {/* Reporter information */}
        <SectionGroup title="Rapportinformation">
          <ActionRow
            icon={User}
            label="Rapporterad av"
            value={report.reportedBy}
            chevron={false}
          />
          <ActionRow
            icon={Calendar}
            iconColor="text-amber-500"
            label="Rapporterad"
            value={formatDate(report.reportedAt)}
            chevron={false}
          />
        </SectionGroup>

        {/* Metadata */}
        <SectionGroup title="Metadata">
          <ActionRow
            icon={Calendar}
            label="Skapad"
            value={formatDate(report.createdAt)}
            chevron={false}
          />
          <ActionRow
            icon={Calendar}
            label="Uppdaterad"
            value={formatDate(report.updatedAt)}
            chevron={false}
          />
        </SectionGroup>

        {/* Photos */}
        <SectionGroup title={`Foton av skadan (${photos.length})`}>
          {photos.length === 0 ? (
            <div className="px-4 py-4 text-center">
              <Camera size={24} className="text-[var(--text-tertiary)] mx-auto mb-2" />
              <p className="text-[15px] text-[var(--text-tertiary)]">Inga foton att visa</p>
            </div>
          ) : (
            <div className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="relative aspect-square overflow-hidden rounded-lg border border-[var(--border-default)] cursor-pointer active:opacity-80 transition-opacity"
                    onClick={() => setSelectedPhotoIndex(index)}
                  >
                    {photoLoadError[photo.id] ? (
                      <div className="w-full h-full flex items-center justify-center bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] text-xs text-center p-2">
                        Kunde inte ladda bilden
                      </div>
                    ) : (
                      <img
                        src={photo.thumbnail}
                        alt={`Foto ${photo.id}`}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(photo.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionGroup>

        {/* Delete */}
        <SectionGroup>
          <ActionRow
            icon={Trash2}
            label="Ta bort skaderapport"
            onClick={handleDelete}
            destructive={true}
            chevron={false}
          />
        </SectionGroup>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ta bort"
        message="Är du säker på att du vill ta bort denna skaderapport? Denna åtgärd kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Photo Viewer */}
      {selectedPhotoIndex !== null && (
        <PhotoViewer
          photos={photos}
          initialPhotoIndex={selectedPhotoIndex}
          onClose={() => setSelectedPhotoIndex(null)}
        />
      )}
    </PageShell>
  );
};

export default DamageReportDetailPage;
